import { useContext } from 'react';
import { Card } from '../components/ui/cards'
import { CheckCircle2 } from "lucide-react";
import Context from '../../common/components/utils/context';

export const Advantages = () => {
  const context = useContext(Context)
    const advantages = {
        "title": "The FoundAt Advantage",
        "subtitle": "Built for the unique demands of infrastructure and field operations",
        "categories": [
            {
                "id": 0,
                "category": "Operational Efficiency",
                "points": [
                "Reduce field visit costs by up to 40% through accurate remote data access",
                "Eliminate delays from missing or inaccessible asset information",
                "Accelerate maintenance and inspection workflows with contextual data"
                ]
            },
            {
                "id": 1,
                "category": "Technical Capability",
                "points": [
                "Industry-only remote AR placement—no on-site scanning required",
                "Precise GO-Locate AR™ positioning works outdoors and indoors",
                "Seamless integration with existing asset management systems"
                ]
            },
            {
                "id": 2,
                "category": "Enterprise Control",
                "points": [
                "AI-powered content moderation ensures governance and compliance",
                "Role-based permissions for secure multi-team collaboration",
                "Indoor location activation on request for controlled deployment"
                ]
            }
        ],
        "technicalNote": {
        "title": "How GO-Locate AR™ Works",
        "paragraph1": "GO-Locate AR™ combines precision geospatial technology with advanced computer vision to deliver data visualization in real-world environments. Our platform connects to your existing asset management systems, databases, and IoT sensors, presenting the right information exactly where your teams need it.",
        "paragraph2": "For indoor deployments, we work with your team to map facilities and activate location services, ensuring the same precision indoors as outdoors. All data transmission is encrypted, and access is governed by your organization's security policies."
        }
    };
  
  return (
    <section ref={context.homepageSections.advantages} id="advantages" className="py-20 bg-gradient-to-br from-home-primary/15 via-home-primary/8 to-home-accent/5 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-home-primary/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-home-accent/20 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.2)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.2)_1px,transparent_1px)] bg-[size:40px_40px] z-20"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-30">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-home-foreground mb-4">
            {advantages.title}
          </h2>
          <p className="text-xl text-home-muted-foreground max-w-2xl mx-auto">
            {advantages.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {advantages.categories.map((category) => {
            return (
              <Card key={category.id} className="p-8 hover:shadow-xl hover:shadow-home-primary/10 transition-all duration-300 backdrop-blur-sm bg-home-card/80">
                <h3 className="text-xl font-bold bg-gradient-to-r from-home-primary to-home-accent bg-clip-text text-transparent mb-6 pb-3 border-b-2 border-home-primary/20">
                  {category.category}
                </h3>
                <ul className="space-y-4">
                  {category.points.map((point: string, i: number) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-home-accent/20 to-home-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-home-foreground">{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })}
        </div>

        <Card className="mt-12 p-8 bg-home-card/80 backdrop-blur-sm border-2 border-home-primary/20 hover:border-home-primary/40 transition-colors">
          <h3 className="text-xl font-semibold text-home-foreground mb-4">{advantages.technicalNote.title}</h3>
          <p className="text-home-muted-foreground mb-4">
            {advantages.technicalNote.paragraph1}
          </p>
          <p className="text-home-muted-foreground">
            {advantages.technicalNote.paragraph2}
          </p>
        </Card>
      </div>
    </section>
  );
};
